import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MediaCard from "@/components/feature/MediaCard";
import SectionHeading from "@/components/feature/SectionHeading";
import EmptyState from "@/components/feature/EmptyState";
import CastList from "@/pages/detail/components/CastList";
import ShareBar from "@/pages/detail/components/ShareBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import { mediaList } from "@/mocks/media";
import { formatPopularity } from "@/utils/media";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = mediaList.find((m) => m.id === id);

  usePageMeta({
    title: item
      ? `${item.title} - 电影电视剧详情 - 聚会的目的在线`
      : "作品不存在 - 聚会的目的在线",
    description: item
      ? `《${item.title}》剧情介绍：${item.synopsis.slice(0, 80)}… 导演 ${item.director}，${item.year} 年${item.region}${item.type === "movie" ? "电影" : "电视剧"}。`
      : "抱歉，你访问的影视作品不存在，请返回首页浏览更多内容。",
    keywords: item
      ? `${item.title},${item.title}剧情介绍,${item.title}演员表,聚会的目的在线`
      : "聚会的目的在线,影视资源",
  });

  if (!item) {
    return (
      <Layout>
        <div className="w-full px-4 md:px-8 py-16 md:py-24">
          <EmptyState
            icon="ri-alert-line"
            title="该影视作品不存在"
            description="你访问的作品可能已下线，或链接有误。返回首页继续浏览其他精彩影视内容吧。"
            actionTo="/"
          />
        </div>
      </Layout>
    );
  }

  const related = mediaList
    .filter(
      (m) =>
        m.id !== item.id &&
        (m.director === item.director || m.genres.some((g) => item.genres.includes(g)))
    )
    .slice(0, 4);

  const infoRows = [
    { label: "导演", value: item.director },
    { label: "类型", value: item.genres.join(" / ") },
    { label: "地区", value: item.region },
    { label: item.type === "movie" ? "片长" : "集数", value: item.duration },
    { label: "上映日期", value: item.releaseDate },
    { label: "状态", value: item.status },
  ];

  return (
    <Layout>
      {/* Hero */}
      <div className="w-full bg-primary-500 text-background-50">
        <div className="w-full px-4 md:px-8 py-6 md:py-10">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-background-50/80 mb-6 flex-wrap">
            <Link to="/" className="hover:text-white cursor-pointer transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={item.type === "movie" ? "/category/movie" : "/category/tv"}
              className="hover:text-white cursor-pointer transition-colors"
            >
              {item.type === "movie" ? "电影" : "电视剧"}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">{item.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 sm:w-48 md:w-56 shrink-0 rounded-lg overflow-hidden border border-white/20">
              <img
                src={item.poster}
                alt={`${item.title} 海报`}
                title={`${item.title} 影视海报`}
                className="w-full aspect-[2/3] object-cover object-top"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                  {item.type === "movie" ? "电影" : "电视剧"}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-white/15 text-white text-xs">
                  {item.status}
                </span>
              </div>
              <h1 className="font-heading font-bold text-2xl md:text-4xl">{item.title}</h1>
              <p className="mt-2 text-sm md:text-base text-background-50/85">{item.tagline}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-50/90">
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400"></i>
                  <strong className="text-white text-base font-bold">
                    {item.rating.toFixed(1)}
                  </strong>
                  <span className="text-background-50/70">分</span>
                </span>
                <span>{item.year}</span>
                <span>{item.region}</span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line text-accent-400"></i>
                  {formatPopularity(item.popularity)} 热度
                </span>
              </div>

              <div className="mt-6">
                <ShareBar title={item.title} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        {/* Info + synopsis */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading font-semibold text-xl text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-7 h-7 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </span>
              剧情简介
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose">
              {item.synopsis}
            </p>
          </div>

          <div className="rounded-lg border border-background-200 p-5 bg-background-100/60 h-fit">
            <h2 className="font-heading font-semibold text-base text-foreground-950 mb-4">
              影片信息
            </h2>
            <dl className="space-y-3">
              {infoRows.map((row) => (
                <div key={row.label} className="flex items-start gap-4 text-sm">
                  <dt className="w-20 shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="text-foreground-900 font-medium">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        {/* Cast */}
        <section className="mt-12 md:mt-16">
          <SectionHeading
            icon="ri-team-line"
            title="演员表"
            subtitle="悬停或点击可查看演员简要信息"
          />
          <CastList cast={item.cast} />
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-12 md:mt-16">
            <SectionHeading
              icon="ri-movie-line"
              title="相关推荐"
              subtitle="同类型或同导演的影视作品"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {related.map((m) => (
                <MediaCard key={m.id} item={m} />
              ))}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}